import { useState } from 'react';
import PageHeader from '../components/PageHeader';
import { API_BASE } from '../apiBase';

const colors = {
  bg: '#f0f1f3',
  cardBg: '#ffffff',
  navy: '#071a52',
  greenDark: '#5cb83c',
  textPrimary: '#1a1a2e',
  textSecondary: '#6b7280',
  border: '#e2e4e8',
  warning: '#d97706',
  warningBg: '#fffbeb',
};

const API = API_BASE;

// Valid bin patterns: AA001-AT064, TUB 01, TRAY 01, WD001, MF*, BINDER*
const VALID_BIN = /^(A[A-T]\d{3}|TUB ?\d{1,2}|TRAY ?\d{1,2}|WD\d{3}|MF.*|BINDER.*)$/i;

function RemarkScanner() {
  const [loading, setLoading] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [groups, setGroups] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalJunk, setTotalJunk] = useState(0);
  const [expanded, setExpanded] = useState(null);

  async function runScan() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/inventories`);
      const json = await res.json();
      const items = json.data || [];
      setTotalItems(items.length);

      const bucket = {};
      let junkCount = 0;
      items.forEach(item => {
        const remark = (item.remarks || '').trim();
        if (!remark || !VALID_BIN.test(remark)) {
          const key = remark || '(empty)';
          if (!bucket[key]) bucket[key] = [];
          bucket[key].push({
            partNo: item.item?.no || '',
            name: item.item?.name || '',
            color: item.color_name || '',
            qty: item.quantity || 0,
          });
          junkCount++;
        }
      });
      setTotalJunk(junkCount);

      const sorted = Object.entries(bucket)
        .map(([remark, items]) => ({ remark, items, count: items.length }))
        .sort((a, b) => b.count - a.count);
      setGroups(sorted);
      setScanned(true);
    } catch (err) {
      console.error('Scan error:', err);
      alert('Scan failed — check console');
    }
    setLoading(false);
  }

  return (
    <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif" }}>
      <PageHeader title="Remark Scanner" icon="◉" />

      <div style={{ marginBottom: '16px', color: colors.textSecondary, fontSize: '0.82rem', lineHeight: 1.5 }}>
        Finds inventory items whose bin remark doesn't match a valid pattern (AA001–AT064, TUB, TRAY, WD, MF, BINDER). These are what clutter up your pick runs.
      </div>

      <button onClick={runScan} disabled={loading} style={{
        width: '100%', padding: '14px 24px',
        background: loading ? colors.border : colors.navy,
        color: loading ? colors.textSecondary : '#ffffff',
        border: 'none', borderRadius: '6px', fontSize: '1rem', fontWeight: 800,
        fontFamily: "'Outfit', sans-serif", cursor: loading ? 'default' : 'pointer',
        marginBottom: '16px',
      }}>
        {loading ? 'Scanning inventory...' : scanned ? 'Scan Again' : 'Run Scan'}
      </button>

      {scanned && (
        <>
          <div style={{
            background: totalJunk > 0 ? colors.warningBg : '#f0fdf4',
            border: `1px solid ${totalJunk > 0 ? '#fde68a' : '#bbf7d0'}`,
            borderLeft: `4px solid ${totalJunk > 0 ? colors.warning : colors.greenDark}`,
            borderRadius: '6px', padding: '16px', marginBottom: '20px',
          }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: totalJunk > 0 ? '#92400e' : colors.greenDark }}>
              {totalJunk} / {totalItems} items flagged
            </div>
            <div style={{ fontSize: '0.75rem', color: colors.textSecondary, marginTop: '4px' }}>
              {groups.length} unique junk remark{groups.length !== 1 ? 's' : ''}
            </div>
          </div>

          {groups.length === 0 && (
            <div style={{ textAlign: 'center', color: colors.greenDark, fontWeight: 700, padding: '20px' }}>
              ✓ All inventory has valid bin remarks!
            </div>
          )}

          {groups.map(group => {
            const isOpen = expanded === group.remark;
            return (
              <div key={group.remark} style={{
                background: colors.cardBg, border: `1px solid ${colors.border}`,
                borderLeft: `3px solid ${colors.warning}`,
                borderRadius: '6px', marginBottom: '10px', overflow: 'hidden',
              }}>
                <div onClick={() => setExpanded(isOpen ? null : group.remark)}
                  style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: '0.95rem', fontWeight: 800, color: colors.textPrimary, fontFamily: 'monospace' }}>
                      {group.remark}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: colors.textSecondary, marginTop: '2px' }}>
                      {group.count} item{group.count !== 1 ? 's' : ''} affected
                    </div>
                  </div>
                  <div style={{ color: colors.textSecondary, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▼</div>
                </div>
                {isOpen && (
                  <div style={{ borderTop: `1px solid ${colors.border}`, padding: '10px 16px', background: '#f9fafb' }}>
                    {group.items.map((item, i) => (
                      <div key={i} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 0', borderBottom: i < group.items.length - 1 ? `1px solid ${colors.border}` : 'none',
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <a href={`https://www.bricklink.com/v2/catalog/catalogitem.page?P=${item.partNo}`}
                            target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: '0.82rem', fontWeight: 700, color: colors.navy, textDecoration: 'none' }}>
                            {item.partNo}
                          </a>
                          <div style={{ fontSize: '0.7rem', color: colors.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.color} · {item.name}
                          </div>
                        </div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 800, color: colors.textPrimary, marginLeft: '10px' }}>
                          x{item.qty}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

export default RemarkScanner;